import { useState, useEffect, useRef } from "react";
import { CopyCodeButton } from "@/components/CopyCode";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, Copy, CheckCircle2, Bluetooth, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatBs, formatUsd, formatCop, formatDate, formatTime } from "@/lib/format";
import { isWebBluetoothAvailable, printTicketViaBluetooth, getSavedPrinter, forgetPrinter, hasCachedPrinter, TICKET_LEGAL_LINES, TICKET_END_MARK } from "@/lib/bluetoothPrint";
import { isWebSerialAvailable, printTicketViaSerial } from "@/lib/serialPrint";
import { isDesktopRawActive, printTicketViaDesktop } from "@/lib/desktopPrint";
import { buildWhatsappTicketUrl } from "@/lib/whatsappShare";
import { getPrinterSettings } from "@/lib/printerConfig";
import { groupTicketPlays, playLabel } from "@/lib/ticketFormat";

// La vista previa conserva los data-testid históricos del recibo
const LEGACY_TID = (n) => (n.startsWith("play-group-") ? `pos-thermal-${n}` : n === "plays" ? "pos-thermal-plays"
  : n === "reprint-mark" ? "pos-receipt-reprint-mark" : n === "identity" ? "pos-receipt-taquilla" : `pos-ticket-${n}`);

// Contenido del recibo térmico (se usa en la vista previa y en la copia de impresión)
function ThermalReceipt({ ticket, paper, lotteryMap, sorteoMap, isReprint, operatorName, taquillaName, validationUrl, idAttr, testId }) {
  const cur = ticket.currency || "BS";
  const bsBase = Number(ticket.total_bs) || 0;
  const ratio = cur === "BS" || bsBase <= 0 ? 1 : (cur === "COP" ? Number(ticket.total_cop) : Number(ticket.total_usd)) / bsBase;
  const amt = (bs) => {
    const v = Number(bs || 0) * ratio;
    return cur === "COP" ? formatCop(Math.round(v)) : cur === "USD" ? formatUsd(v) : formatBs(v);
  };
  const sym = cur === "BS" ? "Bs" : "$";
  const qrSize = paper === "58" ? 96 : 120;
  const tid = (n) => (idAttr ? `${testId}-${n}` : LEGACY_TID(n));

  return (
    <div id={idAttr} className={`thermal-ticket thermal-${paper} receipt-paper rounded-md`} data-testid={testId}>
      <div className="text-center">
        <div className="font-bold leading-tight" data-testid={tid(`brand`)}>
          <span className="tracking-[0.32em] text-[15px]">CONNECT</span>
          <span className="text-[9px] tracking-[0.2em]"> · SISTEMA DE JUEGOS</span>
        </div>
        {isReprint && <div className="text-[10px] font-bold tracking-[0.15em] mt-1" data-testid={tid(`reprint-mark`)}>*** REIMPRESIÓN / COPIA ***</div>}
      </div>
      <div className="mt-1.5 text-[10px] leading-snug" data-testid={tid(`identity`)}>
        {taquillaName ? <div className="th-row"><span>Taquilla</span><span className="font-bold text-right">{taquillaName}</span></div> : null}
        {operatorName ? <div className="th-row"><span>Cajero</span><span className="font-bold text-right">{operatorName}</span></div> : null}
        <div className="th-row"><span>Fecha</span><span className="th-mono text-right">{formatDate(ticket.created_at)}</span></div>
      </div>

      <div className="th-sep-solid" />

      <div className="text-center">
        <div className="text-[9px] tracking-[0.2em]">SERIAL</div>
        <div className="text-[15px] font-bold tracking-[0.18em] th-mono leading-tight mt-0.5" data-testid={tid(`code`)}>{ticket.code}{!idAttr && <CopyCodeButton code={ticket.code} size={13} className="ml-1 print:hidden" />}</div>
      </div>

      <div className="th-sep" />

      <div className="text-[9px] tracking-[0.15em] mb-1">JUGADAS · {ticket.plays.length}</div>
      <div data-testid={tid(`plays`)}>
        {groupTicketPlays(ticket.plays, lotteryMap, sorteoMap).map((g, i) => {
          const multi = g.count > 1;
          return (
            <div key={g.key || i} className="th-play-group py-0.5" data-testid={tid(`play-group-${i}`)}>
              <div className="leading-tight">
                {g.entries.map((e, j) => {
                  const hora = e.scheduled_at ? formatTime(e.scheduled_at) : "";
                  return <div key={j} className="text-[9px] th-mono truncate">{e.lottery_name}{hora ? ` · ${hora}` : ""}</div>;
                })}
              </div>
              <div className="th-row items-baseline mt-0.5">
                <span className="text-[12px] font-bold th-mono">{playLabel(g)}{multi ? ` X${amt(g.amount_bs)}` : ""}</span>
                <span className="text-[11px] font-bold th-mono whitespace-nowrap">{`${sym} ${amt(multi ? g.total_bs : g.amount_bs)}`}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="th-sep-solid" />

      {/* Solo la moneda del ticket: cero reflejo de la divisa opuesta (QA Lotengo) */}
      {cur === "COP" ? (
        <div className="th-row text-[13px] font-bold" data-testid={tid(`total-cop`)}><span>TOTAL COP</span><span className="th-mono">$ {formatCop(ticket.total_cop)}</span></div>
      ) : cur === "USD" ? (
        <div className="th-row text-[13px] font-bold" data-testid={tid(`total-usd`)}><span>TOTAL USD</span><span className="th-mono">$ {formatUsd(ticket.total_usd)}</span></div>
      ) : (
        <div className="th-row text-[13px] font-bold" data-testid={tid(`total-bs`)}><span>TOTAL Bs</span><span className="th-mono">{formatBs(ticket.total_bs)}</span></div>
      )}

      <div className="th-sep" />

      <div className="flex flex-col items-center gap-1" data-testid={tid(`qr`)}>
        <QRCodeCanvas value={validationUrl} size={qrSize} level="M" includeMargin={true} bgColor="#ffffff" fgColor="#000000" className="th-qr" style={{ width: qrSize, height: qrSize }} />
        <div className="text-[8px] text-center break-all leading-tight opacity-80">{validationUrl}</div>
      </div>

      <div className="th-sep" />

      <div className="text-center text-[8.5px] leading-snug" data-testid={tid(`legal`)}>
        {TICKET_LEGAL_LINES.map((l) => <div key={l} className="whitespace-nowrap">{l}</div>)}
      </div>
      <div className="th-end-mark text-center text-[8px] th-mono mt-1.5 opacity-70" data-testid={tid(`end-mark`)}>{TICKET_END_MARK}</div>
      {/* Avance de papel anti-corte (solo copia de impresión): la cuchilla nunca alcanza el texto legal */}
      {idAttr && (
        <div className="print-feed-spacer" style={{ height: "24mm" }} data-testid={tid(`feed-spacer`)} aria-hidden="true">
          <div className="print-feed-dots">- - - - - - - - - - - - - -</div>
        </div>
      )}
    </div>
  );
}

// Recibo optimizado para tiras térmicas 58mm / 80mm con QR de validación.
export function TicketReceiptDialog({ ticket, open, onClose, lotteryMap = {}, sorteoMap = {}, isReprint = false, operatorName = "" }) {
  const printerSettings = getPrinterSettings();
  const [paper, setPaper] = useState(printerSettings.paper_size === "80mm" ? "80" : "58");
  const [btBusy, setBtBusy] = useState(false);
  const autoRef = useRef(null);
  const { data: taqStatus } = useQuery({
    queryKey: ["taquilla-me-status"],
    queryFn: async () => (await api.get("/taquillas/me/status")).data,
    enabled: !!open,
  });
  const taquillaName = taqStatus?.name ? `${taqStatus.name}${taqStatus.code ? ` (${taqStatus.code})` : ""}` : "";

  // Modo impresión de ticket: al imprimir solo se proyecta la copia montada en <body>
  useEffect(() => {
    if (!open || !ticket) return;
    document.body.classList.add("ticket-print-mode");
    return () => document.body.classList.remove("ticket-print-mode");
  }, [open, ticket]);

  const doPrint = async () => {
    // Wrapper Electron (Connect POS Desktop): impresión silenciosa en la térmica configurada
    if (isDesktopRawActive()) {
      const res = await printTicketViaDesktop(ticket, {
        lotteryMap, sorteoMap, isReprint, operatorName, taquillaName, validationUrl,
        paperSize: printerSettings.paper_size, feedLines: printerSettings.feed_lines, cutPaper: printerSettings.cut_paper,
      });
      if (res.ok) toast.success(`Ticket enviado a ${res.device?.name || "impresora"}`);
      else toast.error("Impresión RAW: " + res.error + ". Revisa la tiquera en Impresora.");
      return;
    }
    if (typeof window !== "undefined" && window.electronAPI?.isElectron) {
      try {
        const res = await window.electronAPI.silentPrint();
        if (res?.ok) toast.success("Recibo enviado a la impresora térmica");
        else toast.error(res?.error || "No se pudo imprimir. Configura la impresora con Ctrl+Shift+P.");
      } catch (e) {
        toast.error("Fallo de impresión: " + (e?.message || "desconocido"));
      }
      return;
    }
    // Modo web: silencioso con Chrome --kiosk-printing; si no, diálogo nativo. Diferido al siguiente frame.
    requestAnimationFrame(() => {
      window.print();
      // Kiosco: tras despachar se cierra el recibo para no dejar overlays sobre el POS
      if (printerSettings.kiosk_printing && !isReprint) onClose?.();
    });
  };
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(ticket.code);
      toast.success("Código copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const validationUrl = `${origin}/validar/${ticket?.code || ""}`;

  const btAvailable = typeof window !== "undefined" && isWebBluetoothAvailable() && !window.electronAPI?.isElectron;
  const savedPrinter = btAvailable ? getSavedPrinter() : null;
  const useBluetooth = printerSettings.print_method === "bluetooth" && btAvailable;

  const doBluetoothPrint = async () => {
    setBtBusy(true);
    const tId = toast.loading(hasCachedPrinter() ? "Imprimiendo por Bluetooth…" : savedPrinter ? `Conectando a ${savedPrinter.name}…` : "Selecciona tu impresora Bluetooth…");
    const res = await printTicketViaBluetooth(ticket, {
      lotteryMap, sorteoMap, isReprint, operatorName, taquillaName, validationUrl,
      paperSize: printerSettings.paper_size, feedLines: printerSettings.feed_lines, cutPaper: printerSettings.cut_paper,
    });
    setBtBusy(false);
    toast.dismiss(tId);
    if (res.ok) {
      toast.success(`Ticket enviado a ${res.device?.name || "impresora"}`);
    } else if (!/cancel|NotFoundError|User cancelled/i.test(res.error || "")) {
      toast.error("Bluetooth: " + res.error);
    }
  };
  const useSerial = printerSettings.print_method === "serial" && isWebSerialAvailable() && !window.electronAPI?.isElectron;
  const doSerialPrint = async () => {
    setBtBusy(true);
    const res = await printTicketViaSerial(ticket, {
      lotteryMap, sorteoMap, isReprint, operatorName, taquillaName, validationUrl,
      paperSize: printerSettings.paper_size, feedLines: printerSettings.feed_lines, cutPaper: printerSettings.cut_paper,
      baudRate: printerSettings.serial_baud,
    });
    setBtBusy(false);
    if (res.ok) toast.success(`Ticket enviado a ${res.device?.name || "impresora directa"}`);
    else if (!/cancel|NotFoundError|No port selected/i.test(res.error || "")) toast.error("Impresora directa: " + res.error);
  };
  // Despacho único: ESC/POS directo (Serie o Bluetooth, sin window.print) o navegador
  const handlePrint = () => (useSerial ? doSerialPrint() : useBluetooth ? doBluetoothPrint() : doPrint());

  const shareWhatsapp = () => {
    const url = buildWhatsappTicketUrl(ticket, { validationUrl, operatorName, lotteryMap, sorteoMap });
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Auto-impresión al emitir (solo ventas nuevas), una vez por serial
  useEffect(() => {
    if (!open || !ticket || isReprint) return;
    if (!printerSettings.auto_print_on_sell) return;
    if (autoRef.current === ticket.code) return;
    autoRef.current = ticket.code;
    const t = setTimeout(handlePrint, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticket, isReprint]);

  if (!ticket) return null;

  const receiptProps = { ticket, paper, lotteryMap, sorteoMap, isReprint, operatorName, taquillaName, validationUrl };

  return (
    <>
      {open && createPortal(
        <div id="thermal-print-root" aria-hidden="true">
          <ThermalReceipt {...receiptProps} idAttr="thermal-print-area" testId="pos-thermal-print-area" />
        </div>,
        document.body
      )}
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
        <DialogContent className="bg-card border-border max-w-md p-0 overflow-hidden max-h-[85vh] flex flex-col" data-testid="pos-ticket-receipt-dialog">
          <DialogTitle className="sr-only">{isReprint ? "Reimpresión de ticket" : "Ticket emitido"}</DialogTitle>
          <DialogDescription className="sr-only">Comprobante del ticket {ticket.code}</DialogDescription>

          <div className={`p-4 pr-12 border-b border-border flex items-center gap-2 shrink-0 ${isReprint ? "bg-sky-500/10" : "bg-emerald-500/10"}`}>
            {isReprint ? <Printer size={20} className="text-sky-700 dark:text-sky-300"/> : <CheckCircle2 size={20} className="text-emerald-700 dark:text-emerald-300"/>}
            <div className={`font-display font-semibold ${isReprint ? "text-sky-800 dark:text-sky-200" : "text-emerald-800 dark:text-emerald-200"}`} data-testid="pos-receipt-header">
              {isReprint ? "Reimpresión de ticket" : "Ticket emitido"}
            </div>
            <div className="ml-auto flex items-center gap-1" data-testid="pos-receipt-paper-toggle">
              <span className="text-[11px] text-muted-foreground mr-1">Tira:</span>
              <Button variant={paper === "58" ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs" onClick={() => setPaper("58")} data-testid="pos-receipt-paper-58">58mm</Button>
              <Button variant={paper === "80" ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs" onClick={() => setPaper("80")} data-testid="pos-receipt-paper-80">80mm</Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="pos-receipt-scroll">
            <div className="p-4 flex justify-center bg-secondary/30">
              <ThermalReceipt {...receiptProps} testId="pos-thermal-ticket" />
            </div>
          </div>

          <div className="p-3 border-t border-border flex items-center gap-2 flex-wrap shrink-0">
            <Button variant="outline" size="sm" onClick={copyCode}><Copy size={14} className="mr-1.5"/>Copiar código</Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={btBusy} data-testid="pos-ticket-print-button" data-print-method={useSerial ? "serial" : useBluetooth ? "bluetooth" : "browser"}>
              <Printer size={14} className="mr-1.5"/>Imprimir
            </Button>
            <Button variant="outline" size="sm" onClick={shareWhatsapp} data-testid="pos-ticket-whatsapp-button"
              className="border-emerald-600/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10" title="Compartir por WhatsApp">
              <MessageCircle size={14} className="mr-1.5"/>WhatsApp
            </Button>
            {btAvailable && (
              <Button variant="outline" size="sm" onClick={doBluetoothPrint} disabled={btBusy} data-testid="pos-ticket-bt-print-button"
                className="border-primary/40 text-primary hover:bg-primary/10"
                title={savedPrinter ? `Guardada: ${savedPrinter.name} (click para reusar)` : "Emparejar impresora Bluetooth 58mm"}>
                <Bluetooth size={14} className="mr-1.5"/>
                {btBusy ? "Imprimiendo…" : (savedPrinter ? "BT: " + savedPrinter.name : "Bluetooth 58mm")}
              </Button>
            )}
            {btAvailable && savedPrinter && (
              <Button variant="ghost" size="sm" className="text-muted-foreground text-xs"
                onClick={() => { forgetPrinter(); toast.message("Impresora Bluetooth olvidada"); }}
                data-testid="pos-ticket-bt-forget-button" title="Olvidar impresora guardada">
                Olvidar
              </Button>
            )}
            <Button size="sm" className="ml-auto" onClick={onClose} data-testid="pos-ticket-close">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TicketReceiptDialog;
